import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MetaRow } from '@/ui';
import { StudioButton as Button } from './styles';
import type { CollaborationSelection, StudioResource } from './contracts';
import { studio } from './service';
import { useStudioTask } from './useStudioTask';
import { Field, Note, Row } from './styles';
import { LiveEditor } from './LiveEditor';
export function CollaborationEditor({
  owner,
  body,
  onChoose,
}: {
  owner: string;
  body: string;
  onChoose: (v: CollaborationSelection) => void;
}) {
  const [items, setItems] = useState<StudioResource[]>([]);
  const [next, setNext] = useState<string>();
  const [resource, setResource] = useState<StudioResource>();
  const [title, setTitle] = useState('');
  const [handle, setHandle] = useState('');
  const task = useStudioTask();
  const { setError } = task;
  useEffect(() => {
    let active = true;
    studio.list('post-collaborations').then(
      (r) => {
        if (active) {
          setItems(r.items);
          setNext(r.next);
        }
      },
      () => {
        if (active) setError('Could not load collaborative drafts. Close and reopen to retry.');
      },
    );
    return () => {
      active = false;
    };
  }, [setError]);
  const mine = resource?.data.collaborators?.find((c) => c.person.id === owner);
  const act = (action: string, values: Record<string, unknown> = {}) =>
    resource &&
    void task.run(
      () => studio.action('post-collaborations', resource, action, values),
      setResource,
    );
  return (
    <fieldset
      disabled={task.busy}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'grid', gap: 16 }}
    >
      <Row>
        {items.map((r) => (
          <Button
            key={r.slug}
            onClick={() =>
              void task.run(() => studio.get('post-collaborations', r.slug), setResource)
            }
          >
            {r.title}
          </Button>
        ))}
        {next && (
          <Button
            onClick={() =>
              void task.run(
                () => studio.list('post-collaborations', next),
                (r) => {
                  setItems([...items, ...r.items]);
                  setNext(r.next);
                },
              )
            }
          >
            Load more
          </Button>
        )}
      </Row>
      <Field>
        New draft title
        <input maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Button
        disabled={task.busy || !title.trim()}
        onClick={() =>
          void task.run(
            () => studio.save('post-collaborations', title, { body }),
            (r) => {
              setItems([...items, r]);
              setResource(r);
              setTitle('');
            },
          )
        }
      >
        Create private draft from this text
      </Button>
      {resource && (
        <>
          <h4>{resource.title}</h4>
          {resource.data.collaborators?.map((p) => (
            <Note key={p.person.id}>
              <MetaRow>
                {`@${p.person.handle}`}
                {p.state}
              </MetaRow>
            </Note>
          ))}
          {mine?.state === 'pending' && (
            <Row>
              <Button disabled={task.busy} onClick={() => act('respond', { state: 'accepted' })}>
                Accept invitation
              </Button>
              <Button disabled={task.busy} onClick={() => act('respond', { state: 'declined' })}>
                Decline
              </Button>
            </Row>
          )}
          {resource.data.state === 'draft' &&
            (resource.role === 'owner' || mine?.state === 'accepted') && (
              <LiveEditor
                key={resource.slug}
                owner={owner}
                resource={resource}
                onChoose={onChoose}
              />
            )}
          {resource.role === 'owner' && resource.data.state === 'draft' && (
            <Row>
              <Field>
                Invite co-author
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="Handle"
                />
              </Field>
              <Button
                disabled={task.busy || !handle.trim()}
                onClick={() => act('invite', { handle })}
              >
                Invite
              </Button>
            </Row>
          )}
          {mine?.state === 'accepted' && (
            <Button disabled={task.busy} onClick={() => act('respond', { state: 'withdrawn' })}>
              Leave collaboration
            </Button>
          )}
          <Row>
            <Button
              disabled={task.busy}
              onClick={() =>
                void task.run(() => studio.get('post-collaborations', resource.slug), setResource)
              }
            >
              Refresh draft details
            </Button>
            <Link to={`/collaborations/${encodeURIComponent(resource.slug)}`}>
              Manage invitations and published proposals
            </Link>
          </Row>
        </>
      )}
      {task.error && <p role="alert">{task.error}</p>}
    </fieldset>
  );
}
